import {Component, TextComponent, Object3D} from '@wonderlandengine/api';
import {GameFlowController} from './game-flow-controller.js';
import {NotificationLevel} from '../enums/NotificationLevel.js';

import {RankManager} from './rank-manager.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {timeSinceEpoch} from '../utils/Utils.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {waitForCondition, waitForSeconds} from '../classes/coroutines/YieldInstructions.js';
import {Statistics} from './statistics.js';
import {StatNames} from '../enums/StatNames.js';
import {Localization} from '../utils/Localization.js';
import {RNG} from '../utils/rng.js';
import {MenuPanels, UIModel} from '../models/UIModel.js';
import {GetDailyRewardParams} from './GetDailyRewardParams.js';

//@ts-ignore
import dailyRewardData from './store-data/DailyRewardManager.json' assert {type: 'json'};
import {formatString} from '../ui/utils/utils.js';
import {BlobPrefabs} from './blob-prefabs.js';
import {RankPayout, UnlockRewardData} from '../types/UnlockManagerTypes.js';

export class DailyRewardManager extends Component {
    static TypeName = 'daily-reward-manager';

    static readonly FIRST_TERM_START = 1349240400; // Oct 3, 2012 @ 0:00 AM EST
    static readonly TERM_LENGTH = 60 * 60 * 24; // one day

    private randomDaily: RankPayout[] = dailyRewardData.unlockRewards;

    static readonly SECONDS_PER_MINUTE = 60;
    static readonly SECONDS_PER_HOUR = DailyRewardManager.SECONDS_PER_MINUTE * 60;
    static readonly SECONDS_PER_DAY = DailyRewardManager.SECONDS_PER_HOUR * 24;

    private _countdownId: number;
    private _coroutineManager = new CoroutineManager();
    private _realtimeSinceStartup: number;

    static currentTermStart(): number {
        const currentTime = timeSinceEpoch();
        const delta = currentTime - DailyRewardManager.FIRST_TERM_START;
        const numberOfTerms = Math.floor(delta / DailyRewardManager.TERM_LENGTH);
        return (
            DailyRewardManager.TERM_LENGTH * numberOfTerms +
            DailyRewardManager.FIRST_TERM_START
        );
    }

    init() {
        this._realtimeSinceStartup = Date.now() / 1000;
    }

    start() {
        //UIModel.instance.setNextRewardLabel('');
    }

    onActivate() {
        this._countdownId = this._coroutineManager.addCoroutine(
            new Coroutine(this.countdown())
        );
    }
    onDeactivate() {
        this._coroutineManager.stopCoroutine(this._countdownId);
    }

    private *countdown() {
        yield* waitForCondition(() => Localization.instance.isLoaded);
        yield* waitForCondition(() => BlobPrefabs.instance.isLoaded);
        yield* waitForCondition(() => UIModel.instance.isUILoaded);

        yield null; // wait for one frame

        this._checkReward();

        if (Statistics.instance.getLong(StatNames.TotalGamesPlayed) >= 1) {
            UIModel.instance.setShowNextRewardLabel(true);
        } else {
            UIModel.instance.setShowNextRewardLabel(false);
            return; // no need to continue
        }

        const countdownFormatString = Localization.instance.get('NextFreeGiftIn');
        const hourSingular = Localization.instance.get('HourSingular');
        const hourPlural = Localization.instance.get('HourPlural');
        const minuteSingular = Localization.instance.get('MinuteSingular');
        const minutePlural = Localization.instance.get('MinutePlural');

        let nextTerm =
            DailyRewardManager.currentTermStart() + DailyRewardManager.TERM_LENGTH;
        let counterStart = timeSinceEpoch();

        while (true) {
            let delta = nextTerm - counterStart;

            if (delta < 0) {
                UIModel.instance.setShowNextRewardLabel(false);
                this._checkReward();
                nextTerm =
                    DailyRewardManager.currentTermStart() + DailyRewardManager.TERM_LENGTH;
                counterStart = timeSinceEpoch();
                delta = nextTerm - counterStart;
            }

            const remainingDays = Math.floor(delta / DailyRewardManager.SECONDS_PER_DAY);
            const daySeconds = remainingDays * DailyRewardManager.SECONDS_PER_DAY;
            const remainingHours = Math.floor(
                (delta - daySeconds) / DailyRewardManager.SECONDS_PER_HOUR
            );
            const hourSeconds = remainingHours * DailyRewardManager.SECONDS_PER_HOUR;
            const remainingMinutes =
                Math.floor(
                    (delta - daySeconds - hourSeconds) /
                        DailyRewardManager.SECONDS_PER_MINUTE
                ) + 1; // never say "0 minutes"
            let hours = '';
            if (remainingHours == 1) {
                hours = formatString(hourSingular, remainingHours);
            } else {
                hours = formatString(hourPlural, remainingHours);
            }

            let minutes = '';
            if (remainingMinutes == 1) {
                minutes = formatString(minuteSingular, remainingMinutes);
            } else {
                minutes = formatString(minutePlural, remainingMinutes);
            }

            UIModel.instance.setNextRewardLabel(
                formatString(countdownFormatString, hours, minutes)
            );

            // must not respect timeScale
            const oneMinuteFromNow =
                this._realtimeSinceStartup + 1.0 * DailyRewardManager.SECONDS_PER_MINUTE;
            while (this._realtimeSinceStartup < oneMinuteFromNow) {
                yield null;
            }

            counterStart = timeSinceEpoch();
        }
    }

    private _checkReward() {
        if (Statistics.instance.getLong(StatNames.TotalGamesPlayed) >= 1) {
            const currentTermStart = DailyRewardManager.currentTermStart();

            if (
                currentTermStart >
                Statistics.instance.getLong(StatNames.DailyRewardTimestamp)
            ) {
                UIModel.instance.setShowNextRewardLabel(false);

                Statistics.instance.record(
                    StatNames.DailyRewardTimestamp,
                    currentTermStart
                );

                this.randomAwardFromValid();

                UIModel.instance.setShowNextRewardLabel(true);
            }
        } else {
            UIModel.instance.setShowNextRewardLabel(false);
        }
    }

    private randomAwardFromValid() {
        const rewardIndexes = RNG.randomNonRepeatingValues(
            0,
            this.randomDaily.length,
            this.randomDaily.length
        );

        for (let x = 0; x < this.randomDaily.length; x++) {
            const index = rewardIndexes[x];

            if (
                Statistics.instance.getTransactional(
                    this.randomDaily[index].reward.statToCheckAgainstForValidity
                ) > 0
            ) {
                this._coroutineManager.addCoroutine(
                    new Coroutine(this.showReward(this.randomDaily[index].reward))
                );
                break;
            }
        }
    }

    private *showReward(reward: UnlockRewardData) {
        while (
            GameFlowController.instance.notificationsAllowed !==
                NotificationLevel.Everything ||
            GameFlowController.instance.popupShowing
        ) {
            yield null;
        }

        GetDailyRewardParams.instance.setUp(reward);
        GameFlowController.instance.pushPopupMenu(MenuPanels.GetDailyReward);
    }

    update(dt: number) {
        this._coroutineManager.update(dt);
        this._realtimeSinceStartup += dt;
    }
}
